import type { BasePermission, EmptyObject, EventListener, LoggedEvent } from './loggingTypes';
import { requestIdleCallback } from '@snapchat/polyfills';

// In extreme circumstances we stop logging to avoid memory leaks.
const maxQueueSize = 1e3;

// The default timeout for requestIdleCallback
const defaultIdleTimeout = 500;
export interface BaseEventListenerProps<LoggingContext, CustomEvents> {
  /** If set, will be a check whether to log the event. */
  filter?: (event: LoggedEvent<CustomEvents>, context: Partial<LoggingContext>) => boolean;
  /**
   * Use this to set whether or not the event listener can run asynchronously. If true, logEvent
   * will add the event to a queue and requestIdleCallback will be used to process the queue.
   */
  useAsync?: boolean;
  /** If async, use this to set the timeout value. Defaults to 500 */
  asyncTimeoutMs?: number;
}

export interface Props<
  LoggingContext extends EmptyObject = EmptyObject,
  CustomEvents extends EmptyObject = EmptyObject,
  LoggingPermission extends BasePermission = BasePermission
> extends BaseEventListenerProps<LoggingContext, CustomEvents> {
  requiredPermissions: Array<keyof LoggingPermission>;
}

/** Base event listener that can queue up events. */
export abstract class AbstractEventListener<
  LoggingContext extends EmptyObject = EmptyObject,
  CustomEvents extends EmptyObject = EmptyObject,
  LoggingPermission extends BasePermission = BasePermission
> implements EventListener<LoggingContext, CustomEvents, LoggingPermission>
{
  protected readonly queuedEvents: Array<Parameters<typeof this.logEvent>> = [];
  protected readonly permissions = new Set<keyof LoggingPermission>();
  protected readonly baseProps;

  protected isInitializing = false;
  protected hasInitalized = false;

  constructor(props: Props<LoggingContext, CustomEvents, LoggingPermission>) {
    this.baseProps = props;
  }

  private addToQueue = (
    event: LoggedEvent<CustomEvents>,
    context: Partial<LoggingContext>
  ): void => {
    if (this.queuedEvents.length >= maxQueueSize) {
      console.warn('Logging queue is full. Stopping logging.');
      return;
    }
    this.queuedEvents.push([event, context]);
  };

  /**
   * Checks for required permissions and if they're not met, queues the event for a later time.
   *
   * The queue has a max length, and if it fills up, events will get dropped.
   *
   * @override logEvent
   */
  public logEvent = (event: LoggedEvent<CustomEvents>, context: Partial<LoggingContext>): void => {
    if (!!this.baseProps.filter && !this.baseProps.filter?.(event, context)) return;

    if (!this.hasAllPermissions() || !this.hasInitalized) {
      this.addToQueue(event, context);
    } else if (this.baseProps.useAsync) {
      this.addToQueue(event, context);
      requestIdleCallback(this.logQueuedEvents, {
        timeout: this.baseProps.asyncTimeoutMs ?? defaultIdleTimeout,
      });
    } else {
      this.logEventInternal(event, context);
    }
  };

  /** Checks whether all of the required permissions have been granted. */
  protected hasAllPermissions = (): boolean => {
    for (const permission of this.baseProps.requiredPermissions) {
      if (!this.permissions.has(permission)) {
        return false;
      }
    }

    return true;
  };

  /** Any initialization that the logger has to do should be in the override of this method. */
  protected init(): Promise<void> {
    return Promise.resolve();
  }

  /** Actually logs the event to the underlying system. */
  protected abstract logEventInternal(
    event: LoggedEvent<CustomEvents>,
    context?: Partial<LoggingContext>
  ): void;

  /** Logs any events that have been queued up. */
  protected logQueuedEvents = (): void => {
    for (const [event, context] of this.queuedEvents) {
      this.logEventInternal(event, context);
    }
    this.queuedEvents.length = 0;
  };

  /** @override */
  public allow = async (permission: keyof LoggingPermission): Promise<void> => {
    this.permissions.add(permission);

    if (this.hasAllPermissions() && !this.hasInitalized && !this.isInitializing) {
      this.isInitializing = true;
      await this.init();
      this.hasInitalized = true;
      this.isInitializing = false;
      this.logQueuedEvents();
    }

    return;
  };

  /** @override */
  public deny = (permission: keyof LoggingPermission): Promise<void> => {
    this.permissions.delete(permission);
    return Promise.resolve();
  };

  /** Forces a flush of the internal cache for the collector client. */
  protected abstract flushInternal: () => Promise<unknown>;

  /**
   * Generic flush for all event listener types. Implement listener specific flushes in
   * flushInternal.
   *
   * @override
   */
  public flush = (): Promise<unknown> => {
    if (this.baseProps.useAsync) {
      // We can't flush if we're not initialized.
      this.hasAllPermissions() && this.hasInitalized && this.logQueuedEvents();
    }
    return this.flushInternal();
  };
}
